import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Typography,
  Space,
  message,
  Row,
  Col,
  Switch,
  Divider,
  Alert
} from 'antd';
import {
  SettingOutlined,
  SaveOutlined,
  GlobalOutlined,
  MailOutlined,
  CopyrightOutlined,
  CloudOutlined
} from '@ant-design/icons';
import { apiService } from '../../../services/api';

const { Title, Text } = Typography;

interface SystemSetting {
  setting_key: string;
  setting_value: string;
  description: string;
  is_public: boolean;
}



const Settings: React.FC = () => {
  const [saveLoading, setSaveLoading] = useState(false);
  const [settings, setSettings] = useState<SystemSetting[]>([]);
  const [form] = Form.useForm();

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSettings = async () => {
    try {
      const response = await apiService.adminGetSettings();
      if (response.code === 0) {
        setSettings(response.data);
        
        // 将设置数据转换为表单数据
        const formData: any = {};
        response.data.forEach((setting: SystemSetting) => {
          if (setting.setting_key === 'maintenance_mode') {
            formData[setting.setting_key] = setting.setting_value === '1';
          } else {
            formData[setting.setting_key] = setting.setting_value;
          }
        });
        
        form.setFieldsValue(formData);
      } else {
        message.error(response.message || '获取设置失败');
      }
    } catch (error: any) {
      message.error('获取设置失败');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setSaveLoading(true);
      
      // 转换表单数据为API格式
      const settingsData: Record<string, string> = {};
      Object.keys(values).forEach(key => {
        if (key === 'maintenance_mode') {
          settingsData[key] = values[key] ? '1' : '0';
        } else {
          settingsData[key] = values[key] || '';
        }
      });

      const response = await apiService.adminUpdateSettings(settingsData);
      if (response.code === 0) {
        message.success('设置保存成功');
        loadSettings(); // 重新加载设置
      } else {
        message.error(response.message || '保存设置失败');
      }
    } catch (error: any) {
      message.error('保存设置失败');
    } finally {
      setSaveLoading(false);
    }
  };

  const getSettingDescription = (key: string) => {
    const setting = settings.find(s => s.setting_key === key);
    return setting?.description || '';
  };

  return (
    <div style={{ padding: 24 }}>
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <Title level={2} style={{ margin: 0 }}>
            <Space>
              <SettingOutlined />
              系统设置
            </Space>
          </Title>
          <Text type="secondary">
            配置网站的基本信息和系统参数
          </Text>
        </Col>

        <Col span={24}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
          >
              {/* 网站基本信息 */}
              <Title level={4}>
                <Space>
                  <GlobalOutlined />
                  网站基本信息
                </Space>
              </Title>
              
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="website_title"
                    label="网站标题"
                    extra={getSettingDescription('website_title')}
                    rules={[{ required: true, message: '请输入网站标题' }]}
                  >
                    <Input placeholder="请输入网站标题" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="website_icon_url"
                    label="网站图标URL"
                    extra={getSettingDescription('website_icon_url')}
                    rules={[
                      { required: true, message: '请输入网站图标URL' },
                      { type: 'url', message: '请输入有效的URL' }
                    ]}
                  >
                    <Input placeholder="请输入网站图标URL" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="copyright_info"
                label="版权信息"
                extra={getSettingDescription('copyright_info')}
              >
                <Input placeholder="请输入版权信息" prefix={<CopyrightOutlined />} />
              </Form.Item>

              <Form.Item
                name="contact_email"
                label="联系邮箱"
                extra={getSettingDescription('contact_email')}
                rules={[
                  { required: true, message: '请输入联系邮箱' },
                  { type: 'email', message: '请输入有效的邮箱地址' }
                ]}
              >
                <Input placeholder="请输入联系邮箱" prefix={<MailOutlined />} />
              </Form.Item>

              <Divider />

              {/* 系统配置 */}
              <Title level={4}>
                <Space>
                  <CloudOutlined />
                  系统配置
                </Space>
              </Title>

              <Form.Item
                name="static_resource_cdn"
                label="静态资源CDN"
                extra={getSettingDescription('static_resource_cdn')}
                rules={[{ type: 'url', message: '请输入有效的URL' }]}
              >
                <Input placeholder="请输入静态资源CDN地址" />
              </Form.Item>

              <Form.Item
                name="maintenance_mode"
                label="维护模式"
                extra={getSettingDescription('maintenance_mode')}
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="开启"
                  unCheckedChildren="关闭"
                />
              </Form.Item>

              <Alert
                message="维护模式说明"
                description="开启维护模式后，前台用户将看到维护提示页面，只有管理员可以正常访问。"
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
              />

              <Form.Item style={{ marginBottom: 0 }}>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={saveLoading}
                    icon={<SaveOutlined />}
                  >
                    保存设置
                  </Button>
                  <Button onClick={() => form.resetFields()}>
                    重置
                  </Button>
                </Space>
              </Form.Item>
          </Form>
        </Col>
      </Row>
    </div>
  );
};

export default Settings;
